import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { OpsSettingsType } from '@models/ops-settings';
import { SystemColorSocketService } from '@service/system-color-socket.service';
import { SystemColorService } from '@service/system-color.service';
import { SystemColor, SystemColorType } from 'icreate/common/models/system-color';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-new-color-state-dialog',
    templateUrl: './new-color-state-dialog.component.html',
    styleUrls: ['./new-color-state-dialog.component.css'],
    standalone: false,
})
export class NewColorStateDialogComponent {
    selectedColorId: string | null;
    customColorList: SystemColor[];

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            rangesArray: any[];
            vesselId: string;
        },
        private dialogRef: MatDialogRef<NewColorStateDialogComponent>,
        private router: Router,
        private systemColorService: SystemColorService,
        private systemColorSocketService: SystemColorSocketService
    ) {}

    async ngOnInit() {
        this.customColorList = await this.systemColorService.getByType(this.data.vesselId, SystemColorType.Custom);

        this.systemColorSocketService
            .systemColorCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(newSystemColor => {
                if (newSystemColor.vesselId === this.data.vesselId) {
                    if (newSystemColor.type === SystemColorType.Custom) {
                        this.customColorList = [...this.customColorList, newSystemColor];
                    }
                }
            });

        this.systemColorSocketService
            .systemColorDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(({ systemColorId }) => {
                if (this.selectedColorId == systemColorId) this.selectedColorId = null;
                const findIndex = this.customColorList.findIndex(color => color.id === systemColorId);
                if (findIndex !== -1) {
                    this.customColorList.splice(findIndex, 1);
                }
            });

        this.systemColorSocketService
            .systemColorUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async updatedSystemColor => {
                if (updatedSystemColor.vesselId === this.data.vesselId) {
                    this.customColorList = this.customColorList.map(color => {
                        if (updatedSystemColor.id === color.id) {
                            return updatedSystemColor;
                        } else {
                            return color;
                        }
                    });
                }
            });
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    systemColorSelect(systemColorId: string) {
        if (this.selectedColorId === systemColorId) {
            this.selectedColorId = null;
        } else {
            this.selectedColorId = systemColorId;
        }
    }

    cancel() {
        this.dialogRef.close();
    }

    isCreatingDisabled() {
        return !this.selectedColorId;
    }

    createRange() {
        const selected = this.customColorList.find(systemColor => systemColor.id === this.selectedColorId);

        if (selected) {
            this.dialogRef.close({
                colorId: selected.id,
                min: 0,
                max: 0,
                enabled: true,
            });
        } else {
            this.dialogRef.close();
        }
    }

    goSystemColorPage() {
        const url = this.router.serializeUrl(
            this.router.createUrlTree([`/home/${this.data.vesselId}/settings`], {
                queryParams: { selectedTab: OpsSettingsType.SystemColor },
            })
        );

        window.open(url, '_blank');
    }
}
